<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useSmsStore } from '@/stores/sms'
import type { Conversation } from '@/types/types'
import LoadingIcon from '@/components/LoadingIcon.vue'
import LeadAvatar from '@/components/LeadAvatar.vue'
import { isAiSmsPaused } from '@/lib/aiPause'

const emit = defineEmits<{
  select: [conversation: Conversation]
  newConversation: []
}>()

const smsStore = useSmsStore()
const {
  conversations,
  conversationsLoading,
  hasMoreConversations,
  lastViewedConversationId,
  lastViewedConversationIndex,
  lastScrollPosition,
} = storeToRefs(smsStore)

const listContainer = ref<HTMLElement | null>(null)

const formatTime = (dateString: string | null) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' })
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

const truncateMessage = (message: string | null | undefined, maxLength = 40) => {
  if (!message) return ''
  if (message.length <= maxLength) return message
  return message.substring(0, maxLength) + '...'
}

const handleSelectConversation = (conversation: Conversation) => {
  const index = conversations.value.findIndex((c) => c.id === conversation.id)
  lastViewedConversationId.value = conversation.id
  lastViewedConversationIndex.value = index
  if (listContainer.value) {
    lastScrollPosition.value = listContainer.value.scrollTop
  }
  emit('select', conversation)
}

const scrollToConversation = (conversationId: number) => {
  const element = listContainer.value?.querySelector(`[data-conversation-id="${conversationId}"]`)
  if (element) {
    element.scrollIntoView({ block: 'center' })
    return true
  }
  return false
}

const handleScroll = () => {
  if (!listContainer.value) return

  const { scrollTop, scrollHeight, clientHeight } = listContainer.value
  const nearBottom = scrollTop + clientHeight >= scrollHeight - 100

  if (nearBottom && hasMoreConversations.value && !conversationsLoading.value) {
    smsStore.loadMoreConversations()
  }
}

onMounted(async () => {
  const savedConversationId = lastViewedConversationId.value
  const savedIndex = lastViewedConversationIndex.value
  const savedScrollPos = lastScrollPosition.value

  // Restore scroll position immediately to prevent flash
  if (savedScrollPos > 0 && listContainer.value) {
    listContainer.value.scrollTop = savedScrollPos
  }

  await smsStore.fetchConversations()

  // After fetch, check if conversation position changed
  if (savedConversationId !== null && savedIndex !== null) {
    const newIndex = conversations.value.findIndex((c) => c.id === savedConversationId)
    if (newIndex !== savedIndex) {
      // Position changed, scroll to the conversation
      await nextTick()
      scrollToConversation(savedConversationId)
    } else {
      // Position same, restore original scroll position
      await nextTick()
      if (listContainer.value) {
        listContainer.value.scrollTop = savedScrollPos
      }
    }
  }
})
</script>
<template>
  <div class="relative h-[430px]">
    <div
      ref="listContainer"
      data-testid="conversation-list"
      class="h-full overflow-y-auto border-t border-[#eee] pb-[80px]"
      @scroll="handleScroll"
    >
      <div
        v-if="conversationsLoading && conversations.length === 0"
        class="flex flex-col items-center justify-center h-[200px] text-[#808080]"
      >
        <LoadingIcon />
      </div>

      <div
        v-else-if="conversations.length === 0"
        class="flex flex-col items-center justify-center h-[200px] text-[#808080]"
      >
        <i class="material-icons text-[48px]! text-[#ccc]">sms</i>
        <p>No conversations yet</p>
      </div>

      <div v-else>
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          :data-conversation-id="conversation.id"
          data-testid="conversation-item"
          class="flex items-center px-[16px] py-[12px] border-b border-[#eee] cursor-pointer transition-[background-color] duration-200 hover:bg-[#f5f5f5]"
          @click="handleSelectConversation(conversation)"
        >
          <LeadAvatar :name="conversation.lead?.label" size="lg" class="mr-[12px]" />

          <div class="flex-1 min-w-0">
            <div class="flex justify-between items-center mb-[4px]">
              <span
                class="text-inherit whitespace-nowrap overflow-hidden text-ellipsis"
                :class="{ 'font-semibold': conversation.unread_count > 0 }"
                >{{ conversation.lead?.label || 'Unknown' }}</span
              >
              <span data-testid="timestamp" class="text-[12px] text-[#808080] shrink-0 ml-[8px]">{{
                formatTime(conversation.last_sms_at)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <!-- A prefix rather than an icon: at list width an icon is too
                 small to register, and explaining it needs a hover that a
                 touch screen doesn't have. -->
              <span
                data-testid="last-message"
                class="text-[14px] text-[#666] whitespace-nowrap overflow-hidden text-ellipsis"
                ><span
                  v-if="isAiSmsPaused(conversation)"
                  data-testid="conversation-ai-paused"
                  class="text-[#8b95a2]"
                  >AI paused · </span
                >{{ truncateMessage(conversation.last_sms?.body) || 'No messages' }}</span
              >
              <span
                v-if="conversation.unread_count > 0"
                data-testid="unread-badge"
                class="bg-[#32a8d9] text-white text-[11px] font-semibold px-[6px] py-[2px] rounded-[10px] ml-[8px] shrink-0"
                >{{ conversation.unread_count }}</span
              >
            </div>
            <div
              v-if="conversation.number"
              data-testid="from-number"
              class="text-[11px] text-[#999] mt-[4px]"
            >
              {{ conversation.number?.name || conversation.number?.phone_number_formatted }}
            </div>
          </div>
        </div>

        <div
          v-if="hasMoreConversations && conversationsLoading"
          class="flex justify-center p-[16px]"
        >
          <LoadingIcon />
        </div>
      </div>
    </div>

    <button
      data-testid="fab"
      class="absolute bottom-[16px] right-[16px] w-[56px] h-[56px] rounded-full bg-[#32a8d9] border-none cursor-pointer flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.2)] transition-[background-color,transform] duration-200 hover:bg-[#2899c7] hover:scale-105 active:scale-95"
      @click="emit('newConversation')"
    >
      <i class="material-icons text-white text-[24px]">add</i>
    </button>
  </div>
</template>
